import classes from 'classnames';
import React, { memo, ReactNode, useEffect, useRef } from 'react';
import Card from '@/components/Card/Card';
import Heading from '@/components/Heading/Heading';
import CloseIcon from '@/assets/icons/close.svg?react';
import styles from './Modal.module.scss';

export type Props = {
  onClose?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | MouseEvent,
  ) => void;
  children?: ReactNode;
  backdropClose?: boolean;
  className?: string;
  containerClassName?: string;
  dataTest?: string;
  heading?: string;
  contentClassName?: string;
  disableFadeIn?: boolean;
};

const ModalMemo = memo(function Modal({
  onClose,
  children,
  backdropClose = true,
  className,
  containerClassName,
  dataTest = 'modal',
  heading,
  contentClassName,
  disableFadeIn,
}: Props) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (overlayRef.current && backdropClose) {
      overlayRef.current.addEventListener('mouseup', (event) => {
        if (onClose) {
          onClose(event);
        }
      });
    }
  }, [overlayRef, onClose, backdropClose]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.addEventListener('mouseup', (event) => {
        event.stopPropagation();
      });
    }
  }, [containerRef]);

  return (
    <div
      ref={overlayRef}
      className={classes(
        styles.overlay,
        disableFadeIn && styles['disable-fadein'],
      )}
      data-test={dataTest}
    >
      <div
        className={classes(
          styles.container,
          !disableFadeIn && styles.fade,
          containerClassName,
        )}
      >
        <Card className={classes(styles.card, className)} ref={containerRef}>
          <header>
            {heading && (
              <Heading className={styles.heading} size="s">
                <span
                  className={styles['modal-header']}
                  data-test={`${dataTest}-heading`}
                >
                  {heading}
                </span>
              </Heading>
            )}
            {onClose && (
              <button
                data-test="close-modal"
                onClick={onClose}
                className={styles.close}
                type="button"
                aria-label="close-modal-btn"
              >
                <CloseIcon data-test={`${dataTest}-close-icon`} />
              </button>
            )}
          </header>
          <div
            className={classes(styles.content, contentClassName)}
            data-test={`${dataTest}-content`}
          >
            {children}
          </div>
        </Card>
      </div>
    </div>
  );
});

export default ModalMemo;
